import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
    Badge,
    Button,
    Card,
    CardBody,
    Checkbox,
    HStack,
    IconButton,
    NumberInput,
    NumberInputField,
    Text,
    useColorModeValue,
    useToast,
    VStack
} from '@chakra-ui/react';
import { useState } from 'react';
import { translate } from '../../../i18n';
import { OrganizationSportGameCapacityOptionDto } from '../../../models/organization/dto/organization-sport-game-capacity-option.dto';
import {
    useDeleteOrganizationGameCapacityOption,
    useUpdateOrganizationGameCapacityOption
} from '../../organization/hooks/useOrganizationGameCapacityOptions';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { MAX_BOOKING_CAPACITY, MIN_BOOKING_CAPACITY } from '../../../utils/sport-capacity';
import { buildCapacityWriteWarning } from '../utils/capacity-write-warnings';

interface Props {
  capacityOption: OrganizationSportGameCapacityOptionDto;
}

export default function GameCapacityOptionEditor({ capacityOption }: Props) {
  const { selectedOrganization } = useSelectedOrganization();
  const [isEditing, setIsEditing] = useState(false);
  const [editCapacity, setEditCapacity] = useState(capacityOption.capacity);
  const [editIsDefault, setEditIsDefault] = useState(capacityOption.isDefault);
  const toast = useToast();
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');

  const { mutateAsync: updateCapacityOption, isLoading: isUpdating } = useUpdateOrganizationGameCapacityOption(
    selectedOrganization?.id || '',
    (result) => {
      // Same defect as the delete below, reached through the pencil rather than
      // the trash: re-pointing an option from capacity 2 to capacity 3 retires 2
      // exactly as deleting it would, so every pricing row keyed to 2 becomes
      // money nobody can spend — and capacity 3 is now offered with no prices at
      // all. The operator's action is the more natural of the two, and it was
      // the silent one. Warning, not error: the edit itself is correct and
      // succeeded, and re-pricing afterwards is a legitimate order of
      // operations. `stranded_pricing_rows` reads "configured for that
      // capacity", so it is already true of both rails verbatim.
      //
      // The same save can also point the option at a capacity nothing prices,
      // which is the mirror defect: the picker starts offering a choice whose
      // quote 404s. `buildCapacityWriteWarning` reports both in one toast.
      const warning = buildCapacityWriteWarning(result);
      if (warning) {
        toast({
          title: translate('game_capacity_options.capacity_option_updated'),
          description: warning,
          status: 'warning',
          duration: 10000,
          isClosable: true,
          position: 'top-right',
        });
        setIsEditing(false);
        return;
      }
      toast({
        title: translate('game_capacity_options.capacity_option_updated'),
        status: 'success',
        duration: 3000,
        position: 'top-right',
      });
      setIsEditing(false);
    }
  );

  const { mutateAsync: deleteCapacityOption, isLoading: isDeleting } = useDeleteOrganizationGameCapacityOption(
    selectedOrganization?.id || '',
    (result) => {
      // A plain success toast was the whole defect: retiring the option that a
      // court's ONLY pricing rows are keyed to leaves those rows unreachable,
      // and with no option rows left the browse rail has no capacity to fall
      // back to either — so the sport quietly stops being bookable on that
      // court while the operator is told the delete worked. The delete itself
      // is still correct and still succeeds; what was missing is that anyone
      // was told. Warning rather than error, because cleaning the prices up
      // afterwards is a legitimate order of operations.
      //
      // Deleting the LAST option is the case that also reports unpriced
      // capacities: the sport falls back to the unconfigured accepted set,
      // which is wider than the configured one, so a capacity that
      // configuration was keeping out of reach becomes bookable — with no
      // price behind it.
      const warning = buildCapacityWriteWarning(result);
      if (warning) {
        toast({
          title: translate('game_capacity_options.capacity_option_deleted'),
          description: warning,
          status: 'warning',
          duration: 10000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }
      toast({
        title: translate('game_capacity_options.capacity_option_deleted'),
        status: 'success',
        duration: 3000,
        position: 'top-right',
      });
    }
  );

  const handleUpdate = async () => {
    try {
      await updateCapacityOption({
        id: capacityOption.id,
        data: {
          capacity: editCapacity,
          isDefault: editIsDefault,
        },
      });
    } catch (error: any) {
      toast({
        title: translate('game_capacity_options.error'),
        description: error?.message || translate('game_capacity_options.error_updating'),
        status: 'error',
        duration: 5000,
        position: 'top-right',
      });
    }
  };

  const handleDelete = async () => {
    if (window.confirm(translate('game_capacity_options.confirm_delete'))) {
      try {
        await deleteCapacityOption(capacityOption.id);
      } catch (error: any) {
        toast({
          title: translate('game_capacity_options.error'),
          description: error?.message || translate('game_capacity_options.error_deleting'),
          status: 'error',
          duration: 5000,
          position: 'top-right',
        });
      }
    }
  };

  const handleCancel = () => {
    setEditCapacity(capacityOption.capacity);
    setEditIsDefault(capacityOption.isDefault);
    setIsEditing(false);
  };

  return (
    <Card>
      <CardBody>
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={2}>
            <HStack spacing={2}>
              <Text fontSize="sm" color={subtitleColor}>
                {translate('game_capacity_options.capacity')}
              </Text>
              {capacityOption.isDefault && !isEditing && (
                <Badge colorScheme="brand" size="sm">
                  {translate('game_capacity_options.default')}
                </Badge>
              )}
            </HStack>
            {isEditing ? (
              <VStack align="start" spacing={2}>
                <NumberInput
                  value={editCapacity}
                  onChange={(_, valueAsNumber) => setEditCapacity(valueAsNumber || 1)}
                  min={MIN_BOOKING_CAPACITY}
                  // Above this the money paths reject the booking outright.
                  max={MAX_BOOKING_CAPACITY}
                  size="sm"
                  width="100px"
                >
                  <NumberInputField />
                </NumberInput>
                <Checkbox
                  isChecked={editIsDefault}
                  onChange={(e) => setEditIsDefault(e.target.checked)}
                  size="sm"
                >
                  {translate('game_capacity_options.set_as_default')}
                </Checkbox>
              </VStack>
            ) : (
              <Text fontSize="lg" fontWeight="semibold">
                {capacityOption.capacity} {translate('game_capacity_options.players')}
              </Text>
            )}
          </VStack>

          <HStack spacing={2}>
            {isEditing ? (
              <>
                <Button
                  size="sm"
                  colorScheme="brand"
                  onClick={handleUpdate}
                  isLoading={isUpdating}
                >
                  {translate('game_capacity_options.save')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                >
                  {translate('game_capacity_options.cancel')}
                </Button>
              </>
            ) : (
              <>
                <IconButton
                  aria-label={translate('game_capacity_options.edit_capacity_option')}
                  icon={<EditIcon />}
                  size="sm"
                  variant="outline"
                  onClick={() => setIsEditing(true)}
                />
                <IconButton
                  aria-label={translate('game_capacity_options.delete_capacity_option')}
                  icon={<DeleteIcon />}
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={handleDelete}
                  isLoading={isDeleting}
                />
              </>
            )}
          </HStack>
        </HStack>
      </CardBody>
    </Card>
  );
}
